import React, { useState, useEffect, useRef, useMemo } from "react";
import { FiClock, FiTag, FiTool, FiX, FiChevronDown, FiCheck } from "react-icons/fi";
import { GoTrash } from "react-icons/go";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import DeleteConfirmModalServicePage from "./DeletConfirmModalServicePage";
import AdvancedSettingsModal from "./AdvancedSettingsModal";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getInitialDurationState = (service) => {
  const serviceDuration = toNumber(service?.duration);

  if ([15, 30, 60, 120].includes(serviceDuration)) {
    return {
      duration: serviceDuration,
      customDuration: "",
    };
  }

  return {
    duration: "Other",
    customDuration: serviceDuration ?? "",
  };
};

const getInitialServiceCountState = (service) => {
  const rawServiceCount = service?.service_count;
  const normalizedString = String(rawServiceCount ?? "")
    .trim()
    .toLowerCase();
  const serviceCount = toNumber(rawServiceCount);

  if (
    normalizedString === "unlimited" ||
    rawServiceCount === null ||
    rawServiceCount === undefined ||
    rawServiceCount === ""
  ) {
    return {
      numServices: "Unlimited",
      customNum: "",
    };
  }

  if (["10", "20"].includes(String(serviceCount))) {
    return {
      numServices: String(serviceCount),
      customNum: "",
    };
  }

  if (serviceCount && serviceCount > 0) {
    return {
      numServices: "Other",
      customNum: serviceCount,
    };
  }

  return {
    numServices: "Unlimited",
    customNum: "",
  };
};

const FIELD_LABELS = {
  name: "Service name",
  description: "Description",
  duration: "Duration",
  time_unit: "Time unit",
  price: "Price",
  service_count: "Number of services",
  currency: "Currency",
  on_off: "Status",
  non_field_errors: "General",
  detail: "Error",
};

const formatBackendErrors = (data) => {
  if (!data) return "Something went wrong.";

  if (typeof data === "string") {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => formatBackendErrors(item)).join("\n");
  }

  if (typeof data === "object") {
    const messages = [];

    Object.entries(data).forEach(([key, value]) => {
      const label = FIELD_LABELS[key] || key;

      if (Array.isArray(value)) {
        value.forEach((msg) => {
          messages.push(
            label === "General" || label === "Error" ? msg : `${label}: ${msg}`,
          );
        });
        return;
      }

      if (typeof value === "object" && value !== null) {
        const nested = formatBackendErrors(value);
        if (nested) {
          messages.push(
            label === "General" || label === "Error"
              ? nested
              : `${label}: ${nested}`,
          );
        }
        return;
      }

      if (value) {
        messages.push(
          label === "General" || label === "Error"
            ? String(value)
            : `${label}: ${value}`,
        );
      }
    });

    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  return "Something went wrong.";
};

// Компонент модального окна выбора upsell сервиса
const UpSellingModal = ({ services, selectedServiceId, onSelect, onClose, serviceId, loading: parentLoading }) => {
  const [tempSelectedId, setTempSelectedId] = useState(selectedServiceId);
  const [loading, setLoading] = useState(false);

  // Фильтруем сервисы: показываем только сервисы пользователя, исключая текущий
  const availableServices = useMemo(() => {
    if (!services || !Array.isArray(services)) return [];
    return services.filter(s => s.id !== serviceId);
  }, [services, serviceId]);

  const handleSelect = (id) => {
    setTempSelectedId(tempSelectedId === id ? null : id);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken") || localStorage.getItem("token");
      
      if (!token) {
        throw new Error("No access token found");
      }

      const headers = buildHeaders(token);

      const res = await fetch(`${BASE_URL}/service/upselling-create/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          main_service: serviceId,
          us_selle_service: tempSelectedId,
        }),
      });

      if (!res.ok) {
        let errorData = null;
        try {
          errorData = await res.json();
        } catch {
          const text = await res.text();
          throw new Error(text || "Failed to save upsell.");
        }
        throw new Error(errorData?.detail || "Failed to save upsell.");
      }

      onSelect(tempSelectedId);
      toast.success("Upselling saved successfully!");
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save upsell.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-[500px] max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2 }}
          onClick={handleModalClick}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 className="text-xl font-semibold text-gray-800">Select service to upsell</h3>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition" type="button">
              <FiX size={20} className="text-gray-500" />
            </button>
          </div>

          <div className="p-4 max-h-[500px] overflow-y-auto">
            {availableServices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No other services available for upsell
              </div>
            ) : (
              <div className="space-y-2">
                {availableServices.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleSelect(service.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                      tempSelectedId === service.id
                        ? "border-[#5563F6] bg-[#F7F8FF]"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-gray-800 font-medium text-left">
                      {service.name}
                    </span>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        tempSelectedId === service.id
                          ? "border-[#5563F6] bg-[#5563F6]"
                          : "border-gray-300"
                      }`}
                    >
                      {tempSelectedId === service.id && (
                        <FiCheck size={12} className="text-white" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100 transition">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || parentLoading}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#5563F6] to-[#7C3AED] text-white font-medium hover:shadow-lg transition disabled:opacity-70"
            >
              {loading ? "Saving..." : "Done"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default function EditServiceModal({
  onClose,
  service,
  onUpdated,
  role = "individual",
  allServices = [], // Список всех сервисов пользователя из API
}) {
  const isWorker = role === "worker";
  const modalRef = useRef(null);
  const initialDurationState = getInitialDurationState(service);
  const initialServiceCountState = getInitialServiceCountState(service);

  const [name, setName] = useState(service?.name || "");
  const [price, setPrice] = useState(
    service?.price !== undefined && service?.price !== null
      ? String(service.price)
      : "",
  );
  const [description, setDescription] = useState(service?.description || "");

  const [duration, setDuration] = useState(initialDurationState.duration);
  const [customDuration, setCustomDuration] = useState(
    initialDurationState.customDuration,
  );

  const [numServices, setNumServices] = useState(
    initialServiceCountState.numServices,
  );
  const [customNum, setCustomNum] = useState(
    initialServiceCountState.customNum,
  );

  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showUpselling, setShowUpselling] = useState(false);
  const [upsellingServiceId, setUpsellingServiceId] = useState(null);

  const durationOptions = [
    { label: "15 minutes", value: 15 },
    { label: "30 minutes", value: 30 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 120 },
    { label: "Other", value: "Other" },
  ];

  const numOptions = ["10", "20", "Unlimited", "Other"];

  // Инициализация данных upsell из сервиса
  useEffect(() => {
    if (service) {
      if (service.up_selle?.has_upselle === true && service.up_selle?.upselle_service_id) {
        setUpsellingServiceId(service.up_selle.upselle_service_id);
      } else {
        setUpsellingServiceId(null);
      }
    }
  }, [service]);

  const toggleDropdown = (type) => {
    if (isWorker || loading) return;
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleSelect = (type, value) => {
    if (isWorker || loading) return;

    if (type === "duration") {
      setDuration(value);
      if (value !== "Other") {
        setCustomDuration("");
      }
    }

    if (type === "services") {
      setNumServices(value);
      if (value !== "Other") {
        setCustomNum("");
      }
    }

    setOpenDropdown(null);
  };

  const handleUpsellSelect = (selectedServiceId) => {
    setUpsellingServiceId(selectedServiceId);
  };

  const handleRemoveUpsell = () => {
    setUpsellingServiceId(null);
  };

  const selectedUpsellService = useMemo(() => {
    if (!allServices || !Array.isArray(allServices)) return null;
    return allServices.find(s => s.id === upsellingServiceId);
  }, [allServices, upsellingServiceId]);

  const handleSave = async () => {
    if (isWorker) return;

    const finalDuration =
      duration === "Other"
        ? toNumber(customDuration) || 30
        : toNumber(duration);

    const finalServiceCount =
      numServices === "Other"
        ? toNumber(customNum) || 1
        : numServices === "Unlimited" || !numServices
          ? "Unlimited"
          : toNumber(numServices);

    if (!name.trim() || !price || !description.trim()) {
      toast.error("Please fill all fields before saving.");
      return;
    }

    if (!finalDuration || finalDuration <= 0) {
      toast.error("Please enter a valid duration.");
      return;
    }

    if (
      numServices === "Other" &&
      (!finalServiceCount || Number(finalServiceCount) <= 0)
    ) {
      toast.error("Please enter a valid number of services.");
      return;
    }

    if (Number(price) <= 0) {
      toast.error("Please enter a valid price.");
      return;
    }

    setLoading(true);

    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        localStorage.getItem("token");

      if (!token) {
        throw new Error("No access token found — please log in.");
      }

      const headers = buildHeaders(token);

      // Обновляем основной сервис
      const res = await fetch(
        `${BASE_URL}/service/my-services/${service.id}/`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim(),
            duration: finalDuration,
            time_unit: "minutes",
            price: parseFloat(price),
            service_count: finalServiceCount,
          }),
        },
      );

      if (!res.ok) {
        let errorData = null;

        try {
          errorData = await res.json();
        } catch {
          const text = await res.text();
          throw new Error(text || "Failed to update service.");
        }

        throw new Error(formatBackendErrors(errorData));
      }

      // Обновляем upsell если изменился
      const currentUpsellId = service.up_selle?.has_upselle === true 
        ? service.up_selle?.upselle_service_id 
        : null;
      
      if (upsellingServiceId !== currentUpsellId) {
        if (upsellingServiceId) {
          const upsellRes = await fetch(
            `${BASE_URL}/service/upselling-create/`,
            {
              method: "POST",
              headers: buildHeaders(token),
              body: JSON.stringify({
                main_service: service.id,
                us_selle_service: upsellingServiceId,
              }),
            },
          );

          if (!upsellRes.ok) {
            let errorData = null;
            try {
              errorData = await upsellRes.json();
            } catch {
              const text = await upsellRes.text();
              throw new Error(text || "Failed to update upsell.");
            }
            throw new Error(formatBackendErrors(errorData));
          }
        }
      }

      toast.success("Service updated successfully!");
      onUpdated?.();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to update service.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isWorker || !service?.id) return;

    setLoading(true);

    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        localStorage.getItem("token");

      if (!token) {
        throw new Error("No access token found — please log in.");
      }

      const headers = buildHeaders(token, {
        "Content-Type": undefined,
      });

      const res = await fetch(
        `${BASE_URL}/service/my-services/${service.id}/`,
        {
          method: "DELETE",
          headers,
        },
      );

      if (!res.ok) {
        let errorData = null;

        try {
          errorData = await res.json();
        } catch {
          const text = await res.text();
          throw new Error(text || "Failed to delete service.");
        }

        throw new Error(formatBackendErrors(errorData));
      }

      toast.success("Service deleted successfully!");
      setShowDeleteModal(false);
      onUpdated?.();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to delete service.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <>
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={handleModalClick}
        >
          <motion.div
            ref={modalRef}
            className="relative w-[90%] max-w-[680px] max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-5"
            initial={{ opacity: 0, scale: 0.8, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 30 }}
            transition={{ type: "spring", stiffness: 150, damping: 18 }}
            onClick={handleModalClick}
          >
            <div className="create-service-header edit-service-header">
              <h2 className="edit-modal-title">Edit Service</h2>
              <button
                type="button"
                className="edit-service-close-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
              >
                <FiX size={24} className="edit-service-close-icon" />
              </button>
            </div>

            <div className="create-service-form">
              <div className="create-service-input-row">
                <div className="create-service-input-group">
                  <label>Service Name</label>
                  <input
                    type="text"
                    placeholder="Type Service name"
                    value={name}
                    onChange={(e) => !isWorker && setName(e.target.value)}
                    disabled={isWorker || loading}
                    className={`input ${isWorker || loading ? "disabled" : ""}`}
                  />
                </div>

                <div className="create-service-input-group create-service-dropdown">
                  <label>Duration</label>
                  <div
                    className={`create-service-dropdown-field ${
                      isWorker || loading ? "disabled" : ""
                    }`}
                    onClick={() => toggleDropdown("duration")}
                  >
                    <FiClock className="create-service-icon" />
                    <span className="create-service-dropdown-text">
                      {duration === "Other"
                        ? `${customDuration || "Custom"} min`
                        : `${duration} min`}
                    </span>
                    {!isWorker && !loading && (
                      <FiChevronDown className="create-service-chevron" />
                    )}
                  </div>

                  <AnimatePresence>
                    {openDropdown === "duration" && !isWorker && !loading && (
                      <motion.div
                        className="create-service-dropdown-menu"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2 }}
                      >
                        {durationOptions.map((opt) => (
                          <div
                            key={opt.value}
                            className="create-service-dropdown-item"
                            onClick={() => handleSelect("duration", opt.value)}
                          >
                            <span>{opt.label}</span>
                            {duration === opt.value && (
                              <div className="create-service-success-check">
                                <FiCheck />
                              </div>
                            )}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {duration === "Other" && !isWorker && (
                    <input
                      type="number"
                      className="create-service-custom-input"
                      placeholder="Enter minutes"
                      value={customDuration}
                      onChange={(e) => setCustomDuration(e.target.value)}
                      disabled={loading}
                      min="1"
                    />
                  )}
                </div>
              </div>

              <div className="create-service-input-row">
                <div
                  className={`create-service-input-group ${
                    isWorker || loading ? "opacity-60 pointer-events-none" : ""
                  }`}
                >
                  <label>Service Price</label>
                  <div className="create-service-input-icon-field">
                    <FiTag className="create-service-icon" />
                    <input
                      type="number"
                      placeholder="30"
                      value={price}
                      onChange={(e) => !isWorker && setPrice(e.target.value)}
                      className={`create-service-number-input ${
                        isWorker || loading ? "disabled" : ""
                      }`}
                      disabled={isWorker || loading}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="create-service-input-group create-service-dropdown">
                  <label>№ of Services</label>
                  <div
                    className={`create-service-dropdown-field ${
                      isWorker || loading ? "disabled" : ""
                    }`}
                    onClick={() => toggleDropdown("services")}
                  >
                    <FiTool className="create-service-icon" />
                    <span className="create-service-dropdown-text">
                      {numServices === "Other"
                        ? customNum || "Custom"
                        : numServices || "Unlimited"}
                    </span>
                    {!isWorker && !loading && (
                      <FiChevronDown className="create-service-chevron" />
                    )}
                  </div>

                  <AnimatePresence>
                    {openDropdown === "services" && !isWorker && !loading && (
                      <motion.div
                        className="create-service-dropdown-menu"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2 }}
                      >
                        {numOptions.map((opt) => (
                          <div
                            key={opt}
                            className="create-service-dropdown-item"
                            onClick={() => handleSelect("services", opt)}
                          >
                            <span>{opt}</span>
                            {(numServices || "Unlimited") === opt && (
                              <div className="create-service-success-check">
                                <FiCheck />
                              </div>
                            )}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {numServices === "Other" && !isWorker && (
                    <input
                      type="number"
                      className="create-service-custom-input"
                      placeholder="Enter number"
                      value={customNum}
                      onChange={(e) => setCustomNum(e.target.value)}
                      disabled={loading}
                      min="1"
                    />
                  )}
                </div>
              </div>

              <div className="create-service-input-group create-service-textarea">
                <label>Service Description</label>
                <textarea
                  placeholder="Description"
                  value={description}
                  onChange={(e) => !isWorker && setDescription(e.target.value)}
                  disabled={isWorker || loading}
                  className={`create-service-textarea ${
                    isWorker || loading ? "disabled" : ""
                  }`}
                />
              </div>

              {/* Upselling Section */}
              {!isWorker && (
                <div className="create-service-input-group">
                  <label>Upselling</label>
                  <div className="space-y-2">
                    {!showUpselling && !upsellingServiceId ? (
                      <button
                        type="button"
                        onClick={() => setShowUpselling(true)}
                        className="w-full px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-xl text-[#5563F6] font-medium hover:border-[#5563F6] hover:bg-[#F7F8FF] transition"
                      >
                        + Select service to upsell
                      </button>
                    ) : (showUpselling || upsellingServiceId) && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        {selectedUpsellService ? (
                          <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                            <span className="font-medium text-gray-800">{selectedUpsellService.name}</span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setShowUpselling(true)}
                                className="px-3 py-1 text-sm text-[#5563F6] bg-white border border-gray-200 rounded-lg hover:bg-[#F7F8FF] transition"
                              >
                                Change
                              </button>
                              <button
                                type="button"
                                onClick={handleRemoveUpsell}
                                className="p-1.5 text-gray-400 bg-white border border-gray-200 rounded-lg hover:text-red-500 hover:border-red-200 transition"
                              >
                                <FiX size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="text-gray-500">No service selected</span>
                            <button
                              type="button"
                              onClick={() => setShowUpselling(true)}
                              className="px-3 py-1 text-sm text-[#5563F6] border border-gray-200 rounded-lg hover:bg-[#F7F8FF] transition"
                            >
                              Select
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <div className="flex justify-between items-center">
                <button
                  className="border-2 border-[#5563F6] text-[#5563F6] font-medium rounded-xl px-6 py-3 hover:bg-[#F7F8FF] transition disabled:opacity-70"
                  onClick={() => setShowAdvanced(true)}
                  disabled={loading}
                >
                  Advanced Settings
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="text-[#9A99AB] text-[16px] hover:underline disabled:opacity-70"
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  {!isWorker && (
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-[#5563F6] text-white rounded-xl px-6 py-3 font-medium hover:bg-[#4453E3] transition disabled:opacity-70"
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  )}
                </div>
              </div>

              {!isWorker && service && (
                <div className="flex justify-end mt-2 items-end">
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center gap-1 text-[#9A99AB] underline hover:text-[#302B2B] transition disabled:opacity-70"
                    disabled={loading}
                  >
                    <GoTrash className="w-4 h-4" />
                    Delete Service
                  </button>
                </div>
              )}
            </div>

            {showAdvanced && (
              <AdvancedSettingsModal
                serviceId={service?.id}
                onClose={() => setShowAdvanced(false)}
                onSaved={() => {
                  onUpdated?.();
                }}
                role={role}
              />
            )}

            {showUpselling && (
              <UpSellingModal
                services={allServices}
                selectedServiceId={upsellingServiceId}
                onSelect={handleUpsellSelect}
                onClose={() => setShowUpselling(false)}
                serviceId={service?.id}
                loading={loading}
              />
            )}

            <AnimatePresence>
              {showDeleteModal && !isWorker && (
                <DeleteConfirmModalServicePage
                  onCancel={() => setShowDeleteModal(false)}
                  onDelete={handleDelete}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}